<script lang="ts">
  /**
   * SetPageSheet — the set page, one for every set (Scott, 2026-09-11: "we want
   * to define the set so it's discoverable and understandable: what the strokes
   * are and in what order; then it's great to have some way to compare up to
   * the last 5"). Opened by { kind: "set-history", setId } from the Sets tab
   * strip, a set card, and the ledger (from: "sets-discovered" adds the Back
   * pill to it). Replaced SetHistorySheet (steering round 2: one page per set).
   *
   *   1. Name + "Swum N times".
   *   2. The definition (setPage.setDefinitionView): an exact set draws its
   *      strokes in order as lettered chips through SetGroupRibbon; any other
   *      set shows its one plainDefinition sentence, never an invented example.
   *   3. "Best 25 per stroke · This set" (orch-8 ruling 2b; "This set", not "Any
   *      swim", VP round 3: it read all-time): this set's own best length in
   *      each stroke across its swims, one stroke-coloured chip each. The chips name
   *      the strokes, so they are also the key to the rows' colours below.
   *   4. "Your fastest" pinned (Scott, 06:38: "so you can compare it to your
   *      fastest and that kind of stays up there"), then "Your last N" (N = the
   *      rows shown, VP round 3): one row per swim, newest first, a chip per
   *      length in stroke color. The chips
   *      are the swim's own set group, fetched per row with its saved stroke
   *      fixes applied, so a fixed length reads as it does on Swim; a skeleton
   *      holds the row until it lands. The time sits right-aligned. No second
   *      badge in the list (VP panel, 2026-09-11: it read as two fastests); the
   *      fastest swim wears a small gold dot, pinned and in the list alike (VP
   *      round 3, orch-8, reversible). A swim with a split-length glitch is
   *      dimmed whole, its time struck through, and says "Not counted", so a faster-looking time never
   *      sits unexplained under your fastest (same panel). "Show 5 more" once.
   *      Chips are shaded by pace (Scott: "Shade chips by pace"): brighter for a
   *      length more than 1 s faster than your usual for that stroke in this
   *      set, dimmer for one more than 1 s slower (setPage.paceShade); a
   *      not-counted row and a set swum in mixed pools are not shaded. Scott,
   *      09:4x "Make it obvious": strong tiers (PACE_SHADE) and a one-line key
   *      under the rows, "bright = faster than your usual", shown only when
   *      the rows are shaded.
   *      Tap a row: the swim's panel (date, time, meters, strokes, View) opens
   *      under it; View closes the page and opens the swim.
   */
  import { setsDiscoveredLedger } from "../lib/setsDiscovered";
  import { closeOverlay, openOverlay } from "../lib/overlay";
  import { lockBodyScroll, unlockBodyScroll } from "../lib/native/bodyScrollLock";
  import { sheetDrag } from "../lib/sheetDrag";
  import { bridge } from "../bridge/client";
  import { deriveWorkout, type SetGroup } from "../lib/lapAnalysis";
  import { loadSavedLabels } from "../lib/savedLabels";
  import { getAllSwimFacts } from "../lib/allTimeAggregates";
  import { setHistoryRows, fmtSetValue, fmtSetDate, fmtSetTime, type SetHistoryPoint } from "../lib/setHistory";
  import { SET_DISPLAY } from "../lib/setLibrary";
  import {
    ROWS_STEP,
    ROWS_MAX,
    canShowMore,
    fastestRow,
    fmtRowDate,
    lastSwimRows,
    notCountedCount,
    rowMaxChips,
    setDefinitionView,
    setGroupForPoint,
    strokeBests,
    paceShade,
    usualLengthByStroke,
    templateSetGroup,
    type SetPageRow,
  } from "../lib/setPage";
  import { shortStroke } from "../lib/suspectDeckLogic";
  import { fmtSeconds1 } from "../lib/format";
  import { contrastTextOn, strokeColor } from "../lib/designTokens";
  import { goToTab } from "../lib/tabNav";
  import { haptic } from "../lib/haptics";
  import SetGroupRibbon from "./SetGroupRibbon.svelte";

  interface Props {
    open?: boolean;
    setId: string;
    from?: "sets-discovered";
  }
  let { open = false, setId, from }: Props = $props();

  let name = $derived(SET_DISPLAY[setId]?.displayName ?? "");
  /** Re-read from the cache each time the page opens. */
  let row = $derived.by(() => (open ? (setHistoryRows(getAllSwimFacts()).find((r) => r.setId === setId) ?? null) : null));
  let timesSwum = $derived(row?.timesSwum ?? $setsDiscoveredLedger.find((r) => r.setId === setId)?.count ?? 0);
  let definition = $derived(setDefinitionView(setId));
  let lengthWord = $derived(row?.poolLengthMeters ?? "length");
  /** VP round 4: an average-length set carries its unit on the number ("23.6s /
   *  length", the cards' "avg length" term), not in a heading beside it. */
  let perLength = $derived(row?.basis === "perLength");
  let bests = $derived(row ? strokeBests(row) : []);
  let usual = $derived(row ? usualLengthByStroke(row) : null);

  let count = $state(ROWS_STEP);
  let pinned = $derived(row ? fastestRow(row) : null);
  let notCounted = $derived(row ? notCountedCount(row) : 0);
  let rows = $derived(row ? lastSwimRows(row, count) : []);
  /** The sheet has scrolled: the sticky strip shows its edge (VP round 5: content read as cut off). */
  let scrolled = $state(false);
  /** The open panel: "pin:<key>" for the pinned row, the row key for a last-5 row. */
  let openId = $state<string | null>(null);
  /** Per row key: a set group to draw, null when there is none; absent while loading. */
  let groups = $state<Record<string, SetGroup | null>>({});
  /** Rows already fetching or fetched this open (not reactive: a resolve must not refetch). */
  let pending = new Set<string>();
  let swims = new Map<string, Promise<SetGroup[]>>();

  // The page stays mounted between opens, so reset on close and on a new set:
  // nothing one open touched leaks into the next.
  let shownFor = "";
  $effect(() => {
    const key = open ? setId : "";
    if (key === shownFor) return;
    shownFor = key;
    count = ROWS_STEP;
    openId = null;
    scrolled = false;
    groups = {};
    pending = new Set();
    swims = new Map();
  });

  /** One fetch + derive per swim per open, with the swim's saved stroke fixes. */
  function setGroupsOf(workoutUUID: string): Promise<SetGroup[]> {
    let p = swims.get(workoutUUID);
    if (!p) {
      p = Promise.all([
        bridge.getWorkoutFirehose({ workoutUUID }),
        // A failed label read draws the watch's own strokes rather than no row.
        loadSavedLabels(workoutUUID),
      ]).then(([fh, labels]) => (fh ? deriveWorkout(fh, labels).setGroups : []));
      swims.set(workoutUUID, p);
    }
    return p;
  }

  // Lazy, row by row: each row resolves on its own and its skeleton gives way.
  $effect(() => {
    if (!open || !row) return;
    const id = setId;
    for (const r of pinned ? [pinned, ...rows] : rows) {
      if (pending.has(r.key)) continue;
      pending.add(r.key);
      const { key } = r;
      const { workoutId, dateMs } = r.point;
      setGroupsOf(workoutId)
        .then((sets) => setGroupForPoint(sets, id, dateMs))
        .catch(() => null)
        .then((g) => {
          if (shownFor === id) groups = { ...groups, [key]: g };
        });
    }
  });

  function dismiss() {
    if (from === "sets-discovered") openOverlay({ kind: "sets-discovered" });
    else closeOverlay("set-history");
  }

  function onKey(e: KeyboardEvent) {
    // Only while open: OverlayHost keeps the page mounted.
    if (open && e.key === "Escape") dismiss();
  }

  $effect(() => {
    if (!open) return;
    lockBodyScroll();
    return () => unlockBodyScroll();
  });

  function toggle(id: string) {
    haptic("selection");
    openId = openId === id ? null : id;
  }

  function showMore() {
    haptic("selection");
    count = ROWS_MAX;
  }

  function viewSwim(p: SetHistoryPoint) {
    haptic("light");
    closeOverlay("set-history");
    goToTab("swim", { workoutUUID: p.workoutId });
  }

  const valueText = (seconds: number) => (row ? fmtSetValue(row, seconds) : "");
  const lengthsOf = (p: SetHistoryPoint) => p.strokes.reduce((n, s) => n + s.lengths, 0);
</script>

<svelte:window onkeydown={onKey} />

{#snippet swimRow(r: SetPageRow, id: string)}
  {@const p = r.point}
  {@const g = groups[r.key]}
  <li>
    <button
      type="button"
      class="row"
      class:open={openId === id}
      class:not-counted={r.glitch}
      aria-expanded={openId === id}
      aria-label={`${fmtSetDate(p.dateMs)}, ${valueText(p.valueS)}${perLength ? " per length" : ""}${r.isFastest ? ", your fastest" : ""}${r.glitch ? ", not counted: a length looks mis-timed" : ""}`}
      data-row-key={r.key}
      onclick={() => toggle(id)}
    >
      <span class="row-main">
        <!-- VP round 4 (4 of 5 read the fastest's repeat in the list as a duplicate): the list row says why it repeats; the pinned row's heading already does. -->
        <span class="row-date">{#if r.isFastest}<span class="fast-dot" aria-hidden="true"></span>{/if}{fmtRowDate(p.dateMs)}{#if r.isFastest && !id.startsWith("pin:")}{" · fastest"}{/if}</span>
        {#if g}
          <SetGroupRibbon
            setGroup={g}
            maxChips={rowMaxChips(g.laps.length)}
            chipShade={usual && !r.glitch ? (lap) => paceShade(lap, usual) : undefined}
          />
        {:else if g === null}
          <span class="row-none">—</span>
        {:else}
          <span class="skeleton" style:width={`${Math.max(1, Math.min(lengthsOf(p), 16)) * 16 - 3}px`}></span>
        {/if}
      </span>
      <span class="row-value">
        <span class="time">{valueText(p.valueS)}</span>
        <!-- One line under the time: the not-counted label wins; the unit sits on the rows around it. -->
        {#if r.glitch}<span class="nc">Not counted · mis-timed</span>{:else if perLength}<span class="per">/ length</span>{/if}
      </span>
    </button>

    {#if openId === id}
      <div class="panel" role="region" aria-label={`Swim on ${fmtSetDate(p.dateMs)}`}>
        <div class="panel-top">
          <div>
            <p class="when">{fmtSetDate(p.dateMs)} · {fmtSetTime(p.dateMs)}</p>
            <p class="nums">{p.meters} m · {valueText(p.valueS)}{perLength ? " / length" : ""}</p>
          </div>
          <button type="button" class="view" onclick={() => viewSwim(p)}>View <span aria-hidden="true">›</span></button>
        </div>
        {#if p.strokes.length > 0}
          <ol class="strip" aria-label="Strokes in this swim of the set, in order">
            {#each p.strokes as s (s.stroke)}
              {@const fill = strokeColor(s.stroke)}
              <li class="chip" style={`background:${fill};color:${contrastTextOn(fill)}`}>
                <span class="chip-label">{s.stroke}</span>
                <span class="chip-reps">{s.lengths} × {lengthWord}</span>
              </li>
            {/each}
          </ol>
        {/if}
      </div>
    {/if}
  </li>
{/snippet}

{#if open && name}
  <div
    class="scrim"
    role="presentation"
    onclick={dismiss}
    onkeydown={(e) => e.key === "Enter" && dismiss()}
  ></div>
  <div class="sheet" role="dialog" aria-modal="true" aria-label={name} use:sheetDrag={dismiss} onscroll={(e) => (scrolled = e.currentTarget.scrollTop > 0)}>
    <div class="grabber" aria-hidden="true"></div>
    <div class="content">
      {#if from === "sets-discovered"}
        <div class="hdr">
          <button type="button" class="pill" onclick={dismiss}>‹ Sets you've swum</button>
        </div>
      {/if}
      <!-- orch-9, VP round 4: the name and Done stay on top together as the rows scroll. -->
      <header class="top" class:scrolled>
        <h1 class="title">{name}</h1>
        <button type="button" class="pill" onclick={() => closeOverlay("set-history")}>Done</button>
      </header>
      {#if timesSwum > 0}
        <p class="meta">{timesSwum === 1 ? "Swum once" : `Swum ${timesSwum} times`}{notCounted > 0 ? ` · ${notCounted} not counted` : ""}</p>
      {/if}

      {#if definition.kind === "chips"}
        <div class="definition" role="img" aria-label={`Strokes in order: ${definition.strokes.join(", ")}`}>
          <SetGroupRibbon setGroup={templateSetGroup(definition.strokes)} large letters />
        </div>
      {:else}
        <p class="def">{definition.text}</p>
      {/if}

      {#if row}
        {#if bests.length > 0}
          <div class="rows-head">
            <h2 class="rows-title">Best {lengthWord} per stroke</h2>
            <span class="rows-unit">This set</span>
          </div>
          <ul class="strip bests" data-bests aria-label={`Best ${lengthWord} per stroke in this set`}>
            {#each bests as b (b.stroke)}
              {@const fill = strokeColor(b.stroke)}
              <li class="chip" style={`background:${fill};color:${contrastTextOn(fill)}`}>
                <span class="chip-label">{shortStroke(b.stroke)}</span>
                <span class="chip-reps">{b.seconds === null ? "—" : `${fmtSeconds1(b.seconds)}s`}</span>
              </li>
            {/each}
          </ul>
        {/if}
        {#if pinned}
          <div class="rows-head">
            <h2 class="rows-title">Your fastest</h2>
            {#if !perLength}<span class="rows-unit">Set time</span>{/if}
          </div>
          <ol class="rows" data-rows="fastest">
            {@render swimRow(pinned, `pin:${pinned.key}`)}
          </ol>
        {/if}
        <div class="rows-head">
          <!-- VP round 3: the heading follows the rows shown ("Your last 9" after Show 5 more). -->
          <h2 class="rows-title">{rows.length === 1 ? "Your swim" : `Your last ${rows.length}`}</h2>
          {#if !pinned && !perLength}<span class="rows-unit">Set time</span>{/if}
        </div>
        <ol class="rows" data-rows="last">
          {#each rows as r (r.key)}
            {@render swimRow(r, r.key)}
          {/each}
        </ol>
        <!-- Only when a row on the page is actually shaded: a not-counted row never is (review P2). -->
        {#if usual && usual.size > 0 && ((pinned && !pinned.glitch) || rows.some((r) => !r.glitch))}
          <!-- Scott 09:4x "Make it obvious": the one-line key to the shading. -->
          <p class="pace-key">bright = faster than your usual</p>
        {/if}
        {#if count === ROWS_STEP && canShowMore(row, count)}
          <button type="button" class="more" onclick={showMore}>Show 5 more</button>
        {/if}
      {/if}
    </div>
  </div>
{/if}

<style>
  /* VP round 5 (3 of 5 read the card behind the sheet as this set's own): darker than
     the other sheets' 0.45, lightly blurred, so its numbers can't be read (orch-9,
     this sheet only, reversible). */
  .scrim {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.65);
    -webkit-backdrop-filter: blur(4px);
    backdrop-filter: blur(4px);
    z-index: 50;
    animation: scrim-in var(--dur-card) var(--ease-swim);
    touch-action: none;
  }
  .sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 51;
    max-height: 85vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    background: var(--bg-canvas);
    border-radius: 20px 20px 0 0;
    animation: sheet-in var(--dur-card) var(--ease-swim);
  }
  /* The other sheets' grabber: the cue that a drag down closes it. */
  .grabber {
    position: absolute;
    top: 8px;
    left: 50%;
    width: 36px;
    height: 4px;
    margin-left: -18px;
    border-radius: 999px;
    background: var(--divider-strong);
  }
  .content {
    max-width: 430px;
    margin: 0 auto;
    padding: var(--space-5) var(--space-4) calc(var(--space-6) + env(safe-area-inset-bottom, 0px));
    display: flex;
    flex-direction: column;
  }

  .hdr {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    margin-bottom: var(--space-3);
  }
  /* Big, obvious controls: 17px floor, wet-hands target. */
  .pill {
    font-size: 17px;
    font-weight: 600;
    color: var(--accent-primary);
    background: var(--bg-raised);
    border: none;
    border-radius: 12px;
    padding: 10px 16px;
    min-height: 44px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  /* orch-8 / orch-9 VP round 4 (reversible): the name and Done stay at the top of the
     sheet as the rows scroll under them (5 of 5 panelists misnamed the set once it
     scrolled off; Done scrolled away with it). z-index keeps the strip over the
     shaded chips, which paint as their own layers. */
  .top {
    position: sticky;
    top: 0;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    padding-block: 6px;
    background: var(--bg-canvas);
  }
  /* VP round 5 (3 of 5 read scrolled content as cropped): once the rows slide under the
     strip, a hairline and a soft shadow say it sits on top. None at rest. */
  .top.scrolled {
    box-shadow: 0 1px 0 var(--divider-strong), 0 8px 10px -8px rgba(0, 0, 0, 0.7);
  }
  .title {
    min-width: 0;
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -0.3px;
    color: var(--text-primary);
  }
  .meta {
    margin: 2px 0 0;
    font-size: 15px;
    color: var(--text-muted);
    font-variant-numeric: tabular-nums;
  }

  .definition {
    margin-top: var(--space-4);
  }
  .def {
    margin: var(--space-3) 0 0;
    font-size: 17px;
    line-height: 1.4;
    color: var(--text-muted);
  }

  .rows-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin: var(--space-5) 2px var(--space-2);
  }
  .rows-title {
    margin: 0;
    font: var(--type-title-card);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--text-muted);
  }
  .rows-unit {
    font-size: 13px;
    color: var(--text-muted);
  }

  .rows {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .rows > li {
    border-bottom: 1px solid var(--divider);
  }
  /* One swim: date over its chips on the left, the time on the right. 48 px tall at most. */
  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-2);
    width: 100%;
    min-height: 44px;
    max-height: 48px;
    padding: 4px 6px;
    margin: 0;
    border: 0;
    border-radius: var(--radius-button);
    background: transparent;
    color: inherit;
    font-family: var(--font-text);
    text-align: left;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }
  .row:active,
  .row.open {
    background: var(--bg-raised);
  }
  .row:focus-visible {
    outline: 2px solid var(--accent-yellow);
    outline-offset: 1px;
  }
  .row-main {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .row-date {
    font-size: 12px;
    line-height: 14px;
    color: var(--text-muted);
  }
  /* Your fastest swim: the pinned row and the same swim in the list (VP round 3).
     Horizon Yellow is DESIGN.md's PR colour. */
  .fast-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    margin: 0 5px 1px 0;
    border-radius: 50%;
    background: var(--accent-yellow);
    vertical-align: middle;
  }
  .skeleton {
    display: block;
    height: 22px;
    border-radius: 3px;
    background: var(--bg-raised);
  }
  .row-none {
    font-size: 15px;
    line-height: 22px;
    color: var(--text-dim);
  }
  .row-value {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;
  }
  .time {
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary);
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
  }
  /* A swim with a mis-timed length: chips, date and time dimmed together, the
     label left readable, so the row never reads as a record. */
  .row.not-counted .row-main {
    opacity: 0.4;
  }
  /* orch-8 VP round 4 (reversible): the time struck through too, so it can't pass for a result. */
  .row.not-counted .time {
    opacity: 0.4;
    text-decoration: line-through;
  }
  /* The unit on the number, the row date's size so the right side mirrors the left. */
  .per {
    font-size: 12px;
    line-height: 14px;
    color: var(--text-muted);
  }
  .nc {
    margin-top: 1px;
    padding: 0 4px;
    border-radius: 4px;
    border: 1px solid var(--divider-strong);
    color: var(--text-muted);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    line-height: 12px;
    text-transform: uppercase;
  }

  .pace-key {
    margin: var(--space-2) 0 0;
    font-size: 13px;
    line-height: 1.3;
    color: var(--text-muted);
  }

  .more {
    margin-top: var(--space-3);
    align-self: center;
    min-height: 44px;
    padding: 0 18px;
    border-radius: var(--radius-button);
    border: 1px solid var(--divider-strong);
    background: transparent;
    color: var(--text-primary);
    font-family: var(--font-text);
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  /* The tapped swim, under its row. */
  .panel {
    margin: 4px 0 10px;
    padding: 10px 12px;
    background: var(--bg-raised);
    border-radius: var(--radius-button);
  }
  .panel-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }
  .when {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
  }
  .nums {
    margin: 2px 0 0;
    font-size: 13px;
    color: var(--text-muted);
    font-variant-numeric: tabular-nums;
  }
  .view {
    min-height: 44px;
    padding: 0 14px;
    border-radius: var(--radius-button);
    border: 1px solid var(--accent-primary);
    background: transparent;
    color: var(--accent-primary);
    font-family: var(--font-text);
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    -webkit-tap-highlight-color: transparent;
  }
  .view:focus-visible {
    outline: 2px solid var(--accent-yellow);
    outline-offset: 2px;
  }
  .strip {
    list-style: none;
    margin: 10px 0 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  /* The set's best lengths: the panel's stroke chips, straight under their heading. */
  .strip.bests {
    margin-top: 0;
  }
  .chip {
    display: inline-flex;
    flex-direction: column;
    gap: 1px;
    padding: 6px 10px;
    border-radius: var(--radius-sm);
    min-width: 64px;
  }
  .chip-label {
    font-size: 13px;
    font-weight: 600;
    line-height: 1.2;
  }
  .chip-reps {
    font-size: 12px;
    opacity: 0.9;
    line-height: 1.2;
  }

  @keyframes scrim-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes sheet-in {
    from { transform: translateY(8%); opacity: 0.6; }
    to { transform: translateY(0); opacity: 1; }
  }
</style>
